import type { HTMLAttributes } from 'react';
import { cn } from '@/utils/cn';

// Maps to 01_DESIGN_SYSTEM.md Step 4's status-color table: gray=draft/inactive,
// blue=in_review/submitted, green=approved/compliant/low, yellow=pending/high,
// red=rejected/critical, purple=medium severity.
export type BadgeColor = 'gray' | 'blue' | 'green' | 'yellow' | 'red' | 'purple';

const COLOR_CLASSES: Record<BadgeColor, string> = {
  gray: 'bg-surface-secondary text-text-muted',
  blue: 'bg-primary-light text-primary',
  green: 'bg-success-light text-success-text',
  yellow: 'bg-warning-light text-warning-text',
  red: 'bg-danger-light text-danger-text',
  purple: 'bg-purple-light text-purple-text',
};

export interface BadgeProps extends HTMLAttributes<HTMLSpanElement> {
  color?: BadgeColor;
  /** Adds a small pulsing dot — used for in-progress states like "Evaluating". */
  pulse?: boolean;
  /** Adds a static coloured dot (currentColor) before the label — the standard status-badge marker (01_DESIGN_SYSTEM.md Step 4). */
  dot?: boolean;
}

export function Badge({ className, color = 'gray', pulse = false, dot = false, children, ...props }: BadgeProps) {
  return (
    <span
      className={cn(
        'inline-flex items-center gap-1.5 rounded-full px-2.5 py-0.5 text-xs font-medium',
        COLOR_CLASSES[color],
        className
      )}
      {...props}
    >
      {pulse && <span className="h-1.5 w-1.5 animate-pulse-slow rounded-full bg-current" aria-hidden="true" />}
      {dot && !pulse && <span aria-hidden="true">●</span>}
      {children}
    </span>
  );
}
